import React, { useState, useCallback, useMemo, createContext, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Context 생성
const ToastContext = createContext(null);

// Toast 컴포넌트 (UI)
const Toast = ({ id, message, type, onExit }) => {
    // 토스트 스타일 (Dark Theme에 맞춤)
    const toastStyle = {
        position: 'fixed',
        bottom: 'calc(24px + env(safe-area-inset-bottom, 0px))', // 하단 안전 영역 고려
        left: '50%',
        transform: 'translateX(-50%)',
        padding: '12px 20px',
        backgroundColor: type === 'error' ? '#D32F2F' : '#3A394A', // 타입별 배경색
        color: '#FFFFFF',
        borderRadius: '8px',
        zIndex: 9999, // 다른 UI 위에 표시
        fontFamily: 'Pretendard',
        fontSize: '14px',
        fontWeight: 500,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
        transition: 'opacity 0.3s ease-in-out',
        opacity: 1,
        pointerEvents: 'none', // 클릭 방지
        whiteSpace: 'pre-wrap', // 메시지에 줄바꿈이 있을 경우 처리
    };

    // 3초 후 사라지는 로직
    useEffect(() => {
        const timer = setTimeout(() => {
            onExit(id);
        }, 3000); // 3초 후 onExit 호출

        return () => clearTimeout(timer); // 클린업
    }, [id, onExit]);

    // Portal을 사용하여 body에 직접 렌더링
    return createPortal(
        <div style={toastStyle}>
            {message}
        </div>,
        document.body
    );
};


// Provider 컴포넌트 (상태 관리)
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    // 새 토스트를 추가하는 함수
    const showToast = useCallback((message, type = 'default') => {
        const id = Date.now();
        const newToast = { id, message, type };

        // 새로운 토스트를 목록에 추가
        setToasts(prev => [...prev, newToast]);
    }, []);

    // 토스트를 목록에서 제거하는 함수
    const hideToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    const contextValue = useMemo(() => ({
        // 성공 메시지 표시 함수
        showToastSuccess: (message) => showToast(message, 'success'),
        // 에러 메시지 표시 함수
        showToastError: (message) => showToast(message, 'error'),
    }), [showToast]);

    return (
        <ToastContext.Provider value={contextValue}>
            {children}
            {/* 토스트 목록 렌더링 */}
            {toasts.map(toast => (
                <Toast
                    key={toast.id}
                    id={toast.id}
                    message={toast.message}
                    type={toast.type}
                    onExit={hideToast}
                />
            ))}
        </ToastContext.Provider>
    );
};

// Custom Hook (간편 사용)
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        // Provider 내에서 사용하지 않으면 에러 발생
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};